// Single source of truth for portfolio content. Edit here to update the site.

export const CONTENT = {
  meta: {
    title: "Madan Aritakula — Cloud Security Engineer | AWS · Azure · GCP · OCI | IAM, SIEM pipelines, CSPM",
    description:
      "Madan Aritakula — Cloud Security Engineer | AWS · Azure · GCP · OCI | IAM, SIEM pipelines, CSPM",
  },
  hero: {
    name: "Madan Aritakula",
    role: ["Cloud Security", "Engineer"],
    tag: "AWS · Azure · GCP",
    valueProp:
      "7.5 years securing multi-cloud environments across AWS, Azure & GCP.",
    location: "Hyderabad, India",
    photo: "/madan-headshot.jpg",
    photoAlt: "Madan Aritakula, Cloud Security Engineer",
  },
  contact: {
    email: "aritakulamadan@gmail.com",
    phone: "+91 8374462572",
    linkedin: "linkedin.com/in/madan-aritakula",
    linkedinUrl: "https://www.linkedin.com/in/madan-aritakula",
    whatsapp: "918374462572",
    whatsappMessage:
      "Hi Madan, I came across your portfolio and I'd like to discuss a cloud/security role.",
    resumeUrl: "/Madan_Aritakula_Resume.pdf",
  },
  about: {
    body: [
      "I secure and operate enterprise cloud platforms end-to-end — across AWS, Microsoft Entra ID and Google Cloud — with a specialism in identity governance and posture management.",
      "The differentiator is range: I move fluently between IAM design (SSO / SAML / SCIM, Conditional Access) and the infrastructure layer that IAM ultimately protects — landing zones, Terraform, incident automation, certificate lifecycle.",
      "I replace manual review with scripted controls. Real examples: an access-governance audit across 30+ GitHub orgs, a multi-region SNS→Lambda→Jira incident pipeline, org-level GCP deny policies.",
      "AWS Solutions Architect — Professional. Currently pursuing CKA, AWS DevOps Professional and Terraform Associate.",
    ],
    stats: [
      { label: "Years", value: "7.5+" },
      { label: "Clouds (AWS · Azure · GCP · OCI)", value: "4" },
      { label: "GitHub orgs streaming to SIEM", value: "34" },
      { label: "Monthly cost of the pipelines I run", value: "~0" },
    ],
  },
  skills: [
    {
      group: "Cloud Platforms",
      items: [
        "AWS",
        "IAM",
        "EC2 / VPC",
        "Lambda",
        "ECS / Fargate",
        "ACM",
        "Organizations",
        "Control Tower",
        "Microsoft Azure",
        "Entra ID",
        "Google Cloud",
        "GKE",
        "Org Policies",
        "Audit Logs",
      ],
    },
    {
      group: "IAM & Access",
      items: [
        "SSO / SAML",
        "SCIM provisioning",
        "Conditional Access",
        "MFA enforcement",
        "Least-privilege reviews",
        "Enterprise IAM governance",
        "Cross-account roles",
      ],
    },
    {
      group: "Security & Compliance",
      items: [
        "Rapid7 InsightCloudSec",
        "Vanta",
        "Devo SIEM",
        "AWS Security Hub",
        "GuardDuty",
        "AWS Config",
        "SSL / TLS lifecycle",
        "PKCS#7 / ACM DNS",
        "SOC 2 support",
      ],
    },
    {
      group: "Automation & IaC",
      items: [
        "Terraform",
        "OpenTofu",
        "Spacelift",
        "Ansible",
        "CloudFormation",
        "GitHub Actions",
        "Jenkins",
        "PowerShell",
        "Bash",
      ],
    },
    {
      group: "Containers & DevOps",
      items: [
        "Docker",
        "Kubernetes",
        "GKE",
        "ECS / Fargate",
        "CI/CD pipelines",
        "Drift detection",
        "Observe OPAL",
      ],
    },
    {
      group: "Architecture & Communication",
      items: ["Lucidchart (AWS/GCP icon standards)", "Runbooks", "Solution briefs for leadership", "Confluence"],
    },
  ],
  marquee: [
    "AWS",
    "Azure",
    "GCP",
    "Terraform",
    "Kubernetes",
    "Entra ID",
    "IAM",
    "Rapid7",
    "Vanta",
    "GitHub Enterprise",
    "Lambda",
    "Control Tower",
    "SSO / SAML",
    "SCIM",
    "GuardDuty",
    "SIEM",
    "Ansible",
    "CloudFormation",
  ],
  certifications: {
    achieved: [
      {
        code: "AWS-SAP",
        title: "AWS Certified Solutions Architect",
        level: "Professional",
        issuer: "Amazon Web Services",
      },
      {
        code: "AWS-SAA",
        title: "AWS Certified Solutions Architect",
        level: "Associate",
        issuer: "Amazon Web Services",
      },
    ],
    inProgress: [
      { title: "Certified Kubernetes Administrator", short: "CKA" },
      { title: "AWS DevOps Engineer — Professional", short: "DOP-P" },
      { title: "HashiCorp Terraform Associate", short: "TF-A" },
    ],
  },
  experience: [
    {
      company: "Jaggaer",
      role: "Cloud Security Engineer",
      dates: "May 2025 — Present",
      location: "Hyderabad, India",
      team: "Cloud Security Architecture & Engineering (CSAE)",
      bullets: [
        "Led an enterprise-wide GitHub access-governance audit across 30+ organizations using PowerShell and the GitHub CLI, removing dormant accounts and tightening least-privilege posture.",
        "Owned enterprise SSO/SAML lifecycle with Microsoft Entra ID for multiple SaaS platforms; repaired expired SCIM provisioning for Atlassian Cloud and resolved Conditional Access conflicts.",
        "Engineered an end-to-end production incident pipeline (SNS → Lambda → Jira webhooks) linking Aurora CloudWatch alarms to Jira across three AWS regions, cutting manual triage.",
        "Designed and enforced GCP deny-org policies restricting service-quota increases, paired with a custom service-account audit script for ongoing governance.",
        "Managed SSL/TLS certificate lifecycle end-to-end — CSR generation, ACM DNS validation, PKCS#7/P7B conversions, and customer-facing renewal coordination.",
      ],
    },
    {
      company: "Insight Direct India",
      role: "Cloud Engineer III",
      dates: "Jul 2024 — May 2025",
      bullets: [
        "Implemented AWS Landing Zone and Control Tower for enterprise customers, including Service Control Policies across the Control Tower estate.",
        "Delivered security baselines using AWS Security Hub, GuardDuty and Config across customer environments.",
        "Automated provisioning and drift detection with Terraform, CloudFormation and Ansible — including reverse-engineering existing resources into IaC.",
      ],
    },
    {
      company: "Minfy Technologies",
      role: "Cloud Ops Engineer",
      dates: "Jan 2022 — Jul 2024",
      bullets: [
        "Provided 24/7 L1/L2/L3 oversight for cloud incident response, owning support end-to-end from client through every escalation level.",
        "Managed, configured and resolved AWS security alerts across production and non-production environments.",
        "Drove modern DevOps practices through automation and infrastructure-as-code.",
        "Built out Azure infrastructure for internal environments as part of operational acceptance.",
      ],
      ops: {
        label: "Service operations",
        items: [
          "Core Service Management — change, incident, problem, monitoring & maintenance for 3 core applications",
          "Platform, application and network monitoring",
          "OS patching, backup monitoring/restoration and regular DR / application-recovery testing",
          "Process documentation, monitoring and high-availability solutions",
        ],
      },
      awards: [
        {
          title: "One Minfy Recognition for Customer Success",
          image: "/cert-minfy-customer-success.jpg",
          caption:
            "Recognized by Minfy Co-Founder for outstanding contribution to customer success and exemplifying core cultural values — Jan 2024",
        },
        {
          title: "Most Productive Resource",
          image: "/cert-minfy-productive-resource.jpg",
          caption:
            "Awarded for exceptional performance, enduring commitment, and consistently going the extra mile — by Minfy Co-Founder & Sr. Director",
        },
      ],
    },
    {
      company: "TechGrit",
      role: "Software Engineer",
      dates: "Aug 2021 — Dec 2021",
      bullets: [
        "Provided 24/7 production support across AWS and Linux environments.",
        "Managed AWS EC2 — instances, EBS volumes, AMI backups, volume snapshots and resizing.",
        "Configured Security Groups and ELBs for high availability and controlled inbound/outbound traffic.",
        "Managed IAM users/policies and S3 bucket access controls.",
        "Administered Linux — LVM partitioning, disk management and scheduled automation.",
      ],
    },
    {
      company: "NTT Cloud",
      role: "IOC Analyst",
      dates: "Jul 2019 — Aug 2021",
      bullets: [
        "Provided 24/7 production support for Linux and virtualization environments.",
        "Implemented online backup and restore using snapshot technology; scheduled daily / weekly / monthly backups.",
        "Administered and monitored system performance, disk space and memory.",
        "Managed users, groups and access levels.",
        "Troubleshot issues across support teams for rapid resolution.",
      ],
    },
  ],
  manifesto: [
    {
      n: "01",
      title: "About",
    },
    { n: "02", title: "Skills" },
    { n: "03", title: "Certifications" },
    { n: "04", title: "Experience" },
    { n: "05", title: "Selected Work" },
    { n: "06", title: "Tech Intelligence" },
    { n: "07", title: "Contact" },
  ],
};
